import { useCallback, useSyncExternalStore } from "react";

interface UseSingleTabOptions {
  /** When false, the hook acquires no lock and always returns false. */
  enabled?: boolean;
  /** Name of the shared lock; tabs contending for the same name are duplicates. */
  name?: string;
}

interface TabLock {
  isDuplicate: boolean;
  subscribers: Set<() => void>;
  refCount: number;
  release: () => void;
  releaseTimer?: ReturnType<typeof setTimeout>;
}

// One lock per name, shared across every hook instance in this document. A Web
// Lock represents "this tab is here" — a document-level fact — so it's acquired
// once and held across React mount/unmount churn (StrictMode, route changes)
// instead of being torn down and reacquired per component mount.
const tabLocks = new Map<string, TabLock>();

const setDuplicate = (lock: TabLock, value: boolean) => {
  if (lock.isDuplicate === value) return;
  lock.isDuplicate = value;
  for (const notify of lock.subscribers) notify();
};

const acquire = (name: string, lock: TabLock) => {
  const controller = new AbortController();
  const { signal } = controller;
  lock.release = () => controller.abort();

  // Hold the lock until this tab releases it (last unmount or tab close).
  const hold = () =>
    new Promise<void>((resolve) => {
      if (signal.aborted) resolve();
      else signal.addEventListener("abort", () => resolve());
    });

  navigator.locks
    .request(name, { ifAvailable: true }, (heldLock) => {
      if (heldLock) {
        // No other tab holds it — we're the primary tab.
        setDuplicate(lock, false);
        return hold();
      }
      // Another tab holds it — we're a duplicate. Wait for the lock to free up,
      // then promote this tab to primary.
      setDuplicate(lock, true);
      return navigator.locks.request(name, { signal }, () => {
        setDuplicate(lock, false);
        return hold();
      });
    })
    .catch(() => {
      // Aborted on release, or the lock API failed — fail open so a broken check
      // never blocks the user.
      setDuplicate(lock, false);
    });
};

/**
 * Detects whether another tab in the same browser already holds the app's
 * single-tab lock, using the Web Locks API (`navigator.locks`).
 *
 * The first tab holds an exclusive lock; a later tab that can't get it is a
 * duplicate until the holder closes, at which point it's promoted to primary.
 * State lives in a module-level singleton per lock name so the lock is acquired
 * once per document and survives React mount/unmount churn — the hook only
 * subscribes to it.
 *
 * Returns `false` when disabled or when the Web Locks API is unavailable, so
 * callers never block a user in a browser that can't support the check.
 */
export const useSingleTab = ({ enabled = true, name = "equidefi-tab" }: UseSingleTabOptions = {}): boolean => {
  const active = enabled && typeof navigator !== "undefined" && !!navigator.locks;

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (!active) return () => {};

      let lock = tabLocks.get(name);
      if (!lock) {
        lock = { isDuplicate: false, subscribers: new Set(), refCount: 0, release: () => {} };
        tabLocks.set(name, lock);
        acquire(name, lock);
      }
      // A new subscriber cancels a pending release (e.g. StrictMode remount).
      if (lock.releaseTimer) {
        clearTimeout(lock.releaseTimer);
        lock.releaseTimer = undefined;
      }
      lock.subscribers.add(onStoreChange);
      lock.refCount += 1;

      return () => {
        lock.subscribers.delete(onStoreChange);
        lock.refCount -= 1;
        if (lock.refCount === 0) {
          // Defer release so a synchronous unmount→remount keeps the lock.
          lock.releaseTimer = setTimeout(() => {
            if (lock.refCount === 0) {
              lock.release();
              tabLocks.delete(name);
            }
          }, 0);
        }
      };
    },
    [active, name],
  );

  const getSnapshot = useCallback(() => (active ? (tabLocks.get(name)?.isDuplicate ?? false) : false), [active, name]);

  return useSyncExternalStore(subscribe, getSnapshot, () => false);
};
